import { dmEnv } from '@dm/utils';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { $browserDefaultFontSize, $ratio } from 'src/constant/common';
import { isHospitalHostname } from './env';
import { sendMonitorEvent } from './pageTrack';
import { INQUIRY_MENTALCONSULT_STATIC } from './staticData';
import validate from './validate';

// 由于使用CDN引入，直接从全局对象获取，不再需要动态导入
// CDN 已经在 HTML 中引入了 CryptoJS 和 JSEncrypt
if (!window.CryptoJS) {
  console.warn('CryptoJS not loaded from CDN');
  sendMonitorEvent('CRYPTOJS_CDN_LOAD_FAILED', 'error', 'CryptoJS CDN加载失败，可能影响加密功能', {
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
    url: window.location.href,
  });
}

if (!window.JSEncrypt) {
  console.warn('JSEncrypt not loaded from CDN');
  sendMonitorEvent('JSENCRYPT_CDN_LOAD_FAILED', 'error', 'JSEncrypt CDN加载失败，可能影响RSA加密功能', {
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
    url: window.location.href,
  });
}

export const diagText = (inquiryDiagnosis: any = {}, inquiryTimingType = '') => {
  const { staffDescription = '', diseases = '' } = inquiryDiagnosis;
  if (isMentalConsultInquiry(inquiryTimingType)) {
    return staffDescription;
  }
  const arr: any = [];

  // 根据产品，如果不存在staffDescription，再用diseases
  if (!staffDescription && diseases) {
    JSON.parse(diseases).forEach((d) => {
      const { resName = '' } = d;
      arr.push(resName);
    });
  }

  if (staffDescription) {
    arr.push(staffDescription);
  }
  return arr.length ? arr.join('；') : '无';
};
export const isObject = (obj) => Object.prototype.toString.call(obj) === '[object Object]';
// 处理直营app android 手机 6.0及以下系统
export const zaAppScrollCompatible = () => {
  const ua = navigator.userAgent.toLowerCase();
  // eslint-disable-next-line no-useless-escape
  const match = ua.match(/android\s([0-9\.]*)/);
  const [firstItem = false, version = '0'] = match || [];
  const versions = (version && parseFloat(`${version}`.substring(0, 3))) || 0;
  if (validate.isAndroid() && versions <= 6.3 && dmEnv.isApp()) {
    window.scroll = (x?, y?) => {
      if (x && y) {
        window.scrollTo(x, y);
      } else {
        if (isObject(x)) {
          const { top = 0, bottom = 0 } = x;
          window.scrollTo(x, top || bottom);
        } else {
          window.scrollTo(x || 0, 0);
        }
      }
    };
  }
};
/*
//h5发送消息给小程序
export const h5PostMessageBridgeApplet = (payload?) => {
  let { action = '', data = {} } = payload || {};
  if (action) {
    // wx.miniProgram.navigateBack()
    wx && wx.miniProgram.postMessage && wx.miniProgram.postMessage({ data: { action, data } });
  }
};*/

const secretKey = `-----BEGIN PUBLIC KEY-----
  MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAypLtPFiZgCY77ehMW1r1
  6osorRjCReCPYUtBdSpNcnI5B+MCnXQtmXxmDoXwe5N4lcifnvZiwcfVl4noGJsf
  AqA1uflNj2k4/HyU5/v0lC1xAMr8I3m7xTlyVXTrANNmllM7x+cmt5+7drEueJb9
  +1KVHiZvBZjatre7YlJZ5p2tEYxZf8y5a+8L5BlD2prANngmNLESvDnJlACE4fe2
  8d/CDGkaAiZlk+Kg9FsII1IxblG4OUxoinkasU0TjmWZ8U0G2nERiHAdRjlBnuTk
  iwSufvgp08oLnq165riDFmvCn4V7iC37A4j/nIArPL0+0t0BNiMGd7+9AQ9/JCc7
  SwIDAQAB
  -----END PUBLIC KEY-----`;
export const randomAesKey = (Math.random().toString(11).replace(/\./ig, 'BNiMGd7+9AQ9').toUpperCase()).substring(0, 16);

export const rasSecretKeyEncrypt = () => {
  const encrypt = new window.JSEncrypt();
  encrypt.setPublicKey(secretKey);
  const encrypted = encrypt.encrypt(randomAesKey);
  return encrypted.toString();
};

export const aesDecrypt = (data) => {
  const k = window.CryptoJS.enc.Utf8.parse(randomAesKey);
  const iv1 = window.CryptoJS.enc.Utf8.parse(randomAesKey);
  const bytes = window.CryptoJS.AES.decrypt(data, k, {
    keySize: 128 / 32,
    iv: iv1,
    mode: window.CryptoJS.mode.ECB,
    padding: window.CryptoJS.pad.Pkcs7,
  });
  return bytes.toString(window.CryptoJS.enc.Utf8);

};
export const createSkeletonPlaceholderList = (length, item = {}) => Array.from({ length }).map((k) => ({
  attachmentDomain: {
    attachmentDownloadUrl: '',
  },
  ...item,
}));

/** 是否为购药补方 */
export const isDrugReplenishType = (inquiryTimingType = '') => inquiryTimingType === 'drug_replenish';

// 是否为心理咨询单问诊
export const isMentalConsultInquiry = (inquiryTimingType = '') => (/^mentalConsult$/i.test(inquiryTimingType));

// 问诊 or  心理咨询  静态字段映射
export const inquiryAndMentalConsultStatic = (inquiryTimingType = '') => INQUIRY_MENTALCONSULT_STATIC[inquiryTimingType] || INQUIRY_MENTALCONSULT_STATIC.default;
export const getInquiryOrMentalConsultListUrl = (inquiryTimingType = '') => `/hospital/${isMentalConsultInquiry(inquiryTimingType) ? 'mymentalConsult' : 'myinquiry'}`;

/**
 * @description 简陋的rem计算
 * @param px
 * @returns
 */
export const rem = (px: number) => `${px / $ratio / $browserDefaultFontSize}1rem`;

// 在页面加载和调整大小时更新视口高度
export const setViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  // 设置当前页面的 viewPort 视窗高度到 css 变量中
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
};

/**
 * @description 简化版的rem计算
 * @param px
 * @returns
 */
export const remx = (px: number) => `${px / $browserDefaultFontSize}1rem`;

export const parseURL = (url) => {
  const parser = document.createElement('a');
  const searchObj = {};
  let queries; let key; let value;

  parser.href = url;
  queries = parser.search.replace(/^\?/, '').split('&');
  queries.forEach((query) => {
    key = query.split('=')[0];
    value = query.split('=')[1];
    searchObj[key] = value;
  });

  return {
    protocol: parser.protocol,
    host: parser.host,
    hostname: parser.hostname,
    port: parser.port,
    pathname: parser.pathname,
    searchObj,
    hash: parser.hash,
    search: parser.search,
  };
};

/**
 * 2025-11-07 主子应用合并。继续使用内部跳转
 * - 
 */
export const jumpToPage = ({ history, pathname, search = '', navigateType = 'push' }: { history: any, pathname: string, search: string, navigateType: 'push' | 'replace' }) => {
  if (navigateType === 'push') {
    history.push({ pathname, search });
  } else {
    history.replace({ pathname, search });
  }
};

// 通用跳转方法，适用于banner、优惠券等配置自定义跳转链接
// 功能：1、小程序原生页跳转，不包括三方小程序，因为内嵌webview无法直接跳转三方
// 2、HTTP跳转，包含站内react-router跳转和刷新跳转
export const gotoURL = (url: string, navigateType = 'push') => {
  if (url === '' || url === undefined || url === null) {
    return;
  }
  const { hostname, pathname,search } = parseURL(url);
  // 判断是小程序路径
  if ((url.startsWith('/pages') || url.startsWith('pages')) && validate.isFromMiniApplet()) {
    if (navigateType === 'push') {
      wx.miniProgram.navigateTo({
        url,
      });
    } else {
      wx.miniProgram.redirectTo({
        url,
      });
    }
  } else if (url.startsWith('http') || url.startsWith('https')) {
    // https开头的，如果地址和当前host一样，直接用react-roter的
    if (isHospitalHostname(hostname)) {
      if (navigateType === 'push') {
        window.reactHistory.push({
          pathname,
          search,
        });
      } else {
        window.reactHistory.replace({
          pathname,
          search,
        });
      }
    } else {
      if (navigateType === 'push') {
        window.location.href = url;
      } else {
        window.location.replace(url);
      }
    }
  } else {
    // 一般直接是/开头，直接用react-router
    if (navigateType === 'push') {
      window.reactHistory.push({
        pathname,
        search,
      });
    } else {
      window.reactHistory.replace({
        pathname,
        search,
      });
    }
  }
};

// 过滤掉对象里值是null或者undefined的key
export const filterEmptyValueInObject = (data) => {
  const result: any = {};
  Object.keys(data).forEach((key) => {
    if(data[key] !== null && data[key] !== undefined){
      result[key] = data[key];
    }
  });
  return result;
};

export const isEncoded = (uri) => {
  uri = uri || '';
  return uri !== decodeURIComponent(uri);
};

// 彻底decode一个encode过的字符串
export const fullyDecodeURI = (uri) => {
  while(isEncoded(uri)){
    uri = decodeURIComponent(uri);
  }
  return uri;
};

// 判断是否是JSON字符串
export const isJSONString = (str) => {
  if (typeof str == 'string') {
    try {
      const obj=JSON.parse(str);
      if(typeof obj == 'object' && obj ){
        return true;
      }else{
        return false;
      }

    } catch(e) {
      console.log(`error：${str}!!!${e}`);
      return false;
    }
  }else{
    return false;
  }
};

export const noop = async () => {};

export const appendUrl = (path, search) => path.indexOf('?') !== -1 ? `${ path }&${ search }` : `${ path }?${ search }`;

export const ysbVasCode = ['HYMedicationservices25', 'HYMedicationservices80', 'HYMedicationservices'];

export const isYSB = (vasCode) => ysbVasCode.includes(vasCode);

/* 根据环境给出正确的bizScene */
export const getBizScene = () => {
  if (validate.isFromOwnMiniApplet()) {
    return 'applet';
  }
  return 'h5';
};

export interface ValidationResult {
  isValid: boolean;
  sanitizedValue: string;
  errorMessage: string;
}

export function validateAndSanitizeInput(value: string): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    sanitizedValue: value,
    errorMessage: '',
  };

  // 基础XSS检测 - 只检测实际的HTML标签
  // 修改正则表达式，使其更精确地匹配HTML标签
  const basicXssCheck = /<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/gi.test(value);

  if (basicXssCheck) {
    result.isValid = false;
    result.errorMessage = '检测到不安全的内容，请重新输入';
    return result;
  }

  try {
    // 使用DOMPurify清理内容 - 根据需求允许部分安全标签
    const sanitized = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong'], // 允许基本格式化标签
      ALLOWED_ATTR: ['title', 'alt'], // 允许基本属性
    });

    // 如果清理后的内容与原内容不同
    if (sanitized !== value) {
      result.isValid = false;
      result.errorMessage = '输入内容包含不安全元素，已被过滤';
      result.sanitizedValue = sanitized;
    } else {
      // 输入安全
      result.sanitizedValue = value;
    }
  } catch (error) {
    result.isValid = false;
    result.errorMessage = error instanceof Error
      ? `输入内容处理出错: ${error.message}`
      : '输入内容处理出错，请重试';
    console.error('输入内容处理出错:', error);
  }

  return result;
}


export function safeHTMLComponent(htmlString) {
  return DOMPurify.sanitize(htmlString, {
    FORBID_TAGS: ['a', 'button', 'input', 'textarea', 'select', 'form', 'script', 'iframe', 'object', 'embed', 'style', 'link', 'meta'],
  });
}
/**
 * @description 给一个时间戳，计算剩余时间
 * @param timestamp 时间戳
 * @returns 剩余时间
 * e.g 01天18时34分
 */
export const calculateRemainingTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = Math.max(0, timestamp - now);
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  let result = '';
  
  if (days > 0) {
    result += `${days.toString().padStart(2, '0')}天`;
  }
  
  if (hours > 0 || days > 0) {
    result += `${hours.toString().padStart(2, '0')}时`;
  }
  
  result += `${minutes.toString().padStart(2, '0')}分`;
  
  return result;
};

/**
 * @description 从对象中移除指定的键
 * @param obj 源对象
 * @param keys 要移除的键数组
 * @returns 移除指定键后的新对象
 */
export const cleanObject = <T extends Record<string, any>>(obj: T, keys: string[]): Partial<T> => {
  if (!obj || typeof obj !== 'object') {
    return {};
  }
  
  // 创建源对象的浅拷贝
  const result = { ...obj };
  
  // 移除指定的键
  keys.forEach((key) => {
    if (key in result) {
      delete result[key];
    }
  });
  
  return result;
};

/**
 * @description 将数字转为中文。0-9 对应 零-九
 * @param num 数字
 * @returns 中文
 */
export const numToChinese = (num: number): string => {
  const chnNumChar = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  return chnNumChar[num];
};

/**
 * @description 获取URL查询参数
 * @param url 可选，指定URL，默认为当前页面URL
 * @returns 包含所有查询参数的对象
 */
export interface UrlParams {
  [key: string]: string | number | boolean | object | null | undefined;
}

export const getUrlParams = (url?: string): UrlParams => {
  const search = url ? new URL(url).search : window.location.search;
  const params = new URLSearchParams(search);
  const result: UrlParams = {};

  for (const [key, value] of params.entries()) {
    if (value === '') {
      result[key] = null;
      continue;
    }

    // 尝试解析JSON
    if (value.startsWith('{') && value.endsWith('}') || value.startsWith('[') && value.endsWith(']')) {
      try {
        result[key] = JSON.parse(value);
        continue;
      } catch {
        // 如果不是有效的JSON，继续尝试其他类型
      }
    }

    // 尝试解析数字
    if (!isNaN(Number(value)) && value !== '') {
      result[key] = Number(value);
      continue;
    }

    // 尝试解析布尔值
    if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
      result[key] = value.toLowerCase() === 'true';
      continue;
    }

    // 默认作为字符串处理
    result[key] = decodeURIComponent(value);
  }

  return result;
};

/**
 * @description 获取单个URL查询参数
 * @param key 参数名
 * @param defaultValue 默认值
 * @param url 可选，指定URL，默认为当前页面URL
 * @returns 参数值，如果不存在则返回默认值
 */
export const getUrlParam = <T = string>(
  key: string,
  defaultValue?: T,
  url?: string
): T | string | null => {
  const params = getUrlParams(url);
  if (!(key in params)) {
    return defaultValue !== undefined ? defaultValue : null;
  }
  
  const value = params[key];
  // 如果T是特定类型，尝试进行类型转换
  if (defaultValue !== undefined && typeof defaultValue !== typeof value) {
    // 这里可以根据需要进行更精确的类型转换
    return defaultValue;
  }
  
  return value as unknown as T;
};


export const getDateString = (date: string): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * @description 将URL和参数对象组装成完整的查询字符串URL
 * @param url 基础URL
 * @param options 参数对象，支持字符串和数字类型
 * @returns 组装后的完整URL，格式: "url?param1=value1&param2=value2"
 * @example
 * assembleUrl('/api/test', { code: 1, name: 'test' }) // '/api/test?code=1&name=test'
 * assembleUrl('/api/test?id=123', { code: 1 }) // '/api/test?code=1'
 */
export const assembleUrl = (url: string, options?: Record<string, string | number>): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  if (!options || typeof options !== 'object') {
    return url;
  }

  // 过滤掉空值参数
  const filteredOptions = filterEmptyValueInObject(options);

  // 如果没有有效参数，直接返回原URL
  if (!Object.keys(filteredOptions).length) {
    return url;
  }

  // 分离URL的基础部分和现有查询参数
  const [basePath, existingSearch] = url.split('?');

  // 构建新的查询参数字符串
  const newParams = Object.entries(filteredOptions)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  // 如果URL中已有查询参数，替换现有参数（根据用户需求）
  if (existingSearch) {
    // 解析现有参数
    const existingParams = getUrlParams(`?${existingSearch}`);

    // 合并参数，新参数会覆盖同名的现有参数
    const mergedParams = { ...existingParams, ...filteredOptions };

    // 重新构建查询字符串
    const mergedParamsString = Object.entries(mergedParams)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');

    return mergedParamsString ? `${basePath}?${mergedParamsString}` : basePath;
  }

  // 如果URL中没有查询参数，直接拼接
  return `${basePath}?${newParams}`;
};